import React, { useState, useRef, useEffect } from 'react';
import './ExportButton.css';

export interface ExportButtonProps {
  onPrint?: () => void;
  onCopyText?: () => string;
  onExportCSV?: () => string;
  onExportJSON?: () => Record<string, unknown>;
  calculatorName?: string;
  className?: string;
}

export function ExportButton({
  onPrint,
  onCopyText,
  onExportCSV,
  onExportJSON,
  calculatorName = 'calculation',
  className = '',
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopy = async () => {
    if (!onCopyText) return;
    const text = onCopyText();
    // Close on the click itself, not after the clipboard promise: a second
    // click on "Export" while the write was still pending toggled the menu
    // the wrong way (the same race made the calculator export tests flaky).
    setIsOpen(false);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
    setIsOpen(false);
  };

  const handleExportCSV = () => {
    if (!onExportCSV) return;
    const csv = onExportCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    const filename = calculatorName.toLowerCase().replace(/\s+/g, '-');
    link.download = `${filename}-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleExportJSON = () => {
    if (!onExportJSON) return;
    const data = onExportJSON();
    const json = JSON.stringify({
      ...data,
      exportedAt: new Date().toISOString(),
      exportedFrom: 'BGD Electrical Calculator',
    }, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    const filename = calculatorName.toLowerCase().replace(/\s+/g, '-');
    link.download = `${filename}-${date}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  return (
    <div className={`export-button-container ${className}`} ref={menuRef}>
      <button
        className="export-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Export options"
        aria-expanded={isOpen}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span>Export</span>
      </button>

      {isOpen && (
        <div className="export-menu">
          {onCopyText && (
            <button className="export-menu-item" onClick={handleCopy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
            </button>
          )}
          <button className="export-menu-item" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            <span>Print</span>
          </button>
          {onExportCSV && (
            <button className="export-menu-item" onClick={handleExportCSV}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
              <span>Export CSV</span>
            </button>
          )}
          {onExportJSON && (
            <button className="export-menu-item" onClick={handleExportJSON}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M8 13h2" />
                <path d="M8 17h6" />
              </svg>
              <span>Export JSON</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ExportButton;
